import React, { useEffect, useRef, useState } from 'react';
import styles from './ConversationSettings.module.scss';
import Input from 'component/atom/forms/input/input/Input';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import ColorSelector from './components/color-selector/ColorSelector';
import ThemeSelector from './components/theme-selector/ThemeSelector';
import TextSelector from './components/text-selector/TextSelector';
import StyleSelector from './components/style-selector/StyleSelector';
import Select from 'component/atom/forms/select/select/Select';
import { useFormikContext } from 'formik';
import headerStyle from '../../../../../../assets/image/header-style.png';
import headerAlignment from '../../../../../../assets/image/header-alignment.png';

const ACCEPT = 'image/png,image/jpeg,image/jpg,image/svg+xml,image/webp';

const ConversationSettings = () => {
  const { values, setFieldValue, errors, touched } = useFormikContext();
  const fileInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    const icon = values?.ChatIcon;
    if (!icon) {
      setPreviewUrl(null);
      return;
    }

    let objectUrl;

    if (typeof icon === 'string') {
      if (
        /^data:image\//i.test(icon) ||
        /^https?:\/\//i.test(icon) ||
        icon.startsWith('/')
      ) {
        setPreviewUrl(icon);
        return;
      }
      setPreviewUrl(`data:image/png;base64,${icon}`);
      return;
    }

    if (icon instanceof Blob) {
      objectUrl = URL.createObjectURL(icon);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }

    if (icon instanceof ArrayBuffer) {
      const blob = new Blob([icon], { type: 'image/png' });
      objectUrl = URL.createObjectURL(blob);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }

    if (icon && icon.data) {
      try {
        const arr = Array.isArray(icon.data)
          ? icon.data
          : Array.from(icon.data);
        const u8 = new Uint8Array(arr);
        const type = icon.type || icon.mimeType || 'image/png';
        const blob = new Blob([u8], { type });
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
      } catch {
        setPreviewUrl(null);
        return;
      }
    }

    if (icon && icon.buffer instanceof ArrayBuffer) {
      const blob = new Blob([icon], { type: 'image/png' });
      objectUrl = URL.createObjectURL(blob);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }

    setPreviewUrl(null);
  }, [values?.ChatIcon]);

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFieldValue('ChatIcon', file);
  };

  return (
    <div className={styles['page-settings-card']}>
      <div className={styles['bot-title-settings']}>
        <div
          className={`${styles['input-item-container']} ${styles['title-input']}`}
        >
          <p className={styles['input-title']}>Bot Title</p>
          <Input
            value={values?.ChatbotName}
            label='Chatbot Name'
            maxLength={30}
            inputClassname={styles['input']}
            name='ChatbotName'
            onChange={(e) => setFieldValue('ChatbotName', e.target.value)}
            error={errors?.ChatbotName}
            countSpaces
            touched={touched?.ChatbotName}
          />
        </div>

        <div className={styles['input-item-container']}>
          <div className={styles['input-title-container']}>
            <p className={styles['input-title']}>Chat Icon</p>
            {values?.ShowChatIcon ? (
              <SvgWithBranding
                type='eyeBranding'
                color='#9E9E9E'
                className={styles['eye-icon']}
                onClick={() =>
                  setFieldValue('ShowChatIcon', !values.ShowChatIcon)
                }
              />
            ) : (
              <SvgWithBranding
                type='eyeBrandingStripped'
                color='#9E9E9E'
                className={styles['eye-icon']}
                onClick={() =>
                  setFieldValue('ShowChatIcon', !values.ShowChatIcon)
                }
              />
            )}
          </div>

          <div className={styles['input-card']}>
            <div className={styles['iconWrap']}>
              <img
                src={previewUrl || '/images/bot-blue.svg'}
                alt='chat-icon'
                className={styles['bot-icon-blue']}
              />
            </div>

            <div
              className={styles['upload-container']}
              role='button'
              tabIndex={0}
              onClick={handlePickFile}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') && handlePickFile()
              }
            >
              <SvgWithBranding type='uploadBranding' color='#1A1A1A' />
            </div>

            <input
              ref={fileInputRef}
              type='file'
              accept={ACCEPT}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </div>
        </div>

        <ColorSelector
          title='Icon Background'
          colors={['#BFD6FA', '#B9DBFA', '#D1BFFB']}
          includePicker
          fieldName='IconBackground'
          value={values.IconBackground}
          setFieldValue={setFieldValue}
          onEyeClick={() =>
            setFieldValue('ShowIconBackground', !values.ShowIconBackground)
          }
          viewToggled={values.ShowIconBackground}
        />
      </div>

      <div className={styles['theme-settings']}>
        <ThemeSelector values={values} setFieldValue={setFieldValue} />
        <TextSelector
          title='Header Style'
          fieldName='HeaderStyle'
          options={[
            { label: 'Text', position: 'center' },
            { label: 'Icon + Text', position: 'center' },
          ]}
          values={values}
          setFieldValue={setFieldValue}
          tooltipContent={
            <div className={styles['tooltip-content']}>
              <p>The heading style is the top part of the chatbot.</p>
              <img src={headerStyle} alt='header-style' />
            </div>
          }
        />
        <TextSelector
          title='Title Position'
          fieldName='TitlePosition'
          options={[
            { label: 'Left', position: 'left' },
            { label: 'Center', position: 'center' },
            { label: 'Right', position: 'right' },
          ]}
          values={values}
          setFieldValue={setFieldValue}
          tooltipContent={
            <div className={styles['tooltip-content']}>
              <p>
                Set the alignment of your bot title — choose from left, center,
                or right to match your design.
              </p>
              <img src={headerAlignment} alt='header-alignment' width={366} />
            </div>
          }
        />
      </div>

      <div
        className={`${styles['theme-settings']} ${styles['color-pickers-container']}`}
      >
        <StyleSelector
          title='Style'
          value={values.Style}
          onChange={(val) => setFieldValue('Style', val)}
        />
        <ColorSelector
          title='Accent Color'
          colors={['#0957D0', '#128DEA', '#7C3AED', '#0A7E1D', '#FF9706']}
          includePicker
          fieldName='AccentColor'
          value={values.AccentColor}
          setFieldValue={setFieldValue}
          onEyeClick={() => {}}
          hasView={false}
        />
        <ColorSelector
          title='Icon Colors'
          colors={[values.IconColorPrimary]}
          includePicker
          fieldName='IconColorPrimary'
          value={values.IconColorPrimary}
          setFieldValue={setFieldValue}
          // onEyeClick={() => {}}
          hasView={false}
        />
        <div className={styles['text-style-container']}>
          <p className={styles['input-title']}>Text Style</p>
          <Select
            label='Font Style'
            name='FontFamily'
            value={values.FontFamily}
            onChange={(e) => setFieldValue('FontFamily', e.target.value)}
            placeholder='Choose a font'
            options={[
              { value: 'Helvetica Neue', label: 'Helvetica Neue' },
              { value: 'Montserrat', label: 'Montserrat' },
              { value: 'Roboto', label: 'Roboto' },
              { value: 'Raleway', label: 'Raleway' },
              { value: 'Poppins', label: 'Poppins' },
            ]}
          />
        </div>
      </div>

      <div className={styles['message-area']}>
        <p className={styles['input-title']}>Message Area</p>
        <Input
          value={values?.PlaceholderText}
          label='Placeholder Text'
          inputContainerClassName={styles['message-area-input']}
          maxLength={80}
          inputClassname={styles['input']}
          name='PlaceholderText'
          onChange={(e) => setFieldValue('PlaceholderText', e.target.value)}
          countSpaces={true}
          error={errors?.PlaceholderText}
          touched={touched?.PlaceholderText}
        />
      </div>
    </div>
  );
};

export default ConversationSettings;
